import { acceptHMRUpdate, defineStore } from "pinia";
import type {
  Training,
  TrainingCreatePayload,
  TrainingUpdatePayload,
  TrainingVerifyPayload,
  TrainingFilters,
} from "~/types/training";

interface TrainingState {
  myTrainings: Training[];
  managerTrainings: Training[];
  priorityTrainings: Training[];
}

export const useTrainingStore = defineStore("training", () => {
  const auth = useAuthStore();
  const toast = useToast();

  const state = ref<TrainingState>({
    myTrainings: [],
    managerTrainings: [],
    priorityTrainings: [],
  });

  // ==================== User Training Actions ====================

  const fetchMyTrainings = async () => {
    try {
      const resp = await auth.api<Training[]>("get", "/training");
      state.value.myTrainings = resp;
      return resp;
    } catch (error: any) {
      console.error("Error fetching trainings:", error);
      const description = error.data?.message || error.message || "Could not fetch trainings.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const getMyTraining = async (id: string): Promise<Training> => {
    try {
      const resp = await auth.api<Training>("get", `/training/${id}`);
      return resp;
    } catch (error: any) {
      console.error("Error fetching training:", error);
      const description = error.data?.message || error.message || "Could not fetch training.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const createTraining = async (data: TrainingCreatePayload | FormData) => {
    try {
      const resp = await auth.api<Training>("post", "/training", data as any);
      addOrUpdateMyTrainingLocal(resp);
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: "Training added successfully",
        color: "success",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      console.error("Error creating training:", error);
      const description = error.data?.message || error.message || "Could not create training.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const updateTraining = async (id: string, data: TrainingUpdatePayload | FormData) => {
    try {
      const resp = await auth.api<Training>("put", `/training/${id}`, data as any);
      addOrUpdateMyTrainingLocal(resp);
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: "Training updated successfully",
        color: "success",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      console.error("Error updating training:", error);
      const description = error.data?.message || error.message || "Could not update training.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const deleteTraining = async (id: string) => {
    try {
      await auth.api("delete", `/training/${id}`);
      removeMyTrainingLocal(id);
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: "Training deleted successfully",
        color: "success",
        duration: 2500,
      });
      return true;
    } catch (error: any) {
      console.error("Error deleting training:", error);
      const description = error.data?.message || error.message || "Could not delete training.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  // ==================== Manager Training Actions ====================

  const fetchManagerTrainings = async (filters?: TrainingFilters) => {
    try {
      const resp = await auth.api<{ data: Training[]; meta: any }>("get", "/training/manager", {
        queryString: filters,
      });
      state.value.managerTrainings = resp.data;
      return resp;
    } catch (error: any) {
      console.error("Error fetching manager trainings:", error);
      const description = error.data?.message || error.message || "Could not fetch trainings.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const verifyTraining = async (id: string, data: TrainingVerifyPayload) => {
    try {
      const resp = await auth.api<Training>("post", `/training/${id}/verify`, data as any);
      addOrUpdateManagerTrainingLocal(resp);
      removePriorityTrainingLocal(resp.id);
      const statusMessage = data.status === "verified" ? "verified" : "rejected";
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: `Training ${statusMessage}`,
        color: data.status === "verified" ? "success" : "warning",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      console.error("Error verifying training:", error);
      const description = error.data?.message || error.message || "Could not verify training.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const fetchPriorityTrainings = async () => {
    try {
      const resp = await auth.api<Training[]>("get", "/training/priority");
      state.value.priorityTrainings = resp;
      return resp;
    } catch (error: any) {
      console.error("Error fetching priority trainings:", error);
      const description =
        error.data?.message || error.message || "Could not fetch priority trainings.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const sendReminder = async (id: string) => {
    try {
      const resp = await auth.api<Training>("post", `/training/${id}/remind`);
      addOrUpdateManagerTrainingLocal(resp);
      if (resp.reminder_sent) {
        removePriorityTrainingLocal(resp.id);
      } else {
        addOrUpdatePriorityTrainingLocal(resp);
      }
      toast.add({
        icon: "i-lucide-bell",
        title: "Reminder Sent",
        description: "Staff member has been notified",
        color: "warning",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      console.error("Error sending reminder:", error);
      const description = error.data?.message || error.message || "Could not send reminder.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  // ==================== Local Mutation Methods (for SSE handlers) ====================

  const updateMyTrainingLocal = (updatedTraining: Training | Partial<Training>) => {
    const index = state.value.myTrainings.findIndex((t) => t.id === updatedTraining.id);
    if (index !== -1) {
      state.value.myTrainings[index] = { ...state.value.myTrainings[index], ...updatedTraining };
    }
  };

  const addOrUpdateMyTrainingLocal = (training: Training | Partial<Training>) => {
    const index = state.value.myTrainings.findIndex((t) => t.id === training.id);
    if (index !== -1) {
      state.value.myTrainings[index] = { ...state.value.myTrainings[index], ...training };
    } else {
      state.value.myTrainings.unshift(training as Training);
    }
  };

  const removeMyTrainingLocal = (id: string) => {
    state.value.myTrainings = state.value.myTrainings.filter((t) => t.id !== id);
  };

  const addOrUpdateManagerTrainingLocal = (training: Training | Partial<Training>) => {
    const index = state.value.managerTrainings.findIndex((t) => t.id === training.id);
    if (index !== -1) {
      state.value.managerTrainings[index] = {
        ...state.value.managerTrainings[index],
        ...training,
      };
    } else {
      state.value.managerTrainings.unshift(training as Training);
    }
  };

  const removeManagerTrainingLocal = (id: string) => {
    state.value.managerTrainings = state.value.managerTrainings.filter((t) => t.id !== id);
  };

  const addOrUpdatePriorityTrainingLocal = (training: Training | Partial<Training>) => {
    const index = state.value.priorityTrainings.findIndex((t) => t.id === training.id);
    if (index !== -1) {
      state.value.priorityTrainings[index] = {
        ...state.value.priorityTrainings[index],
        ...training,
      };
    } else {
      state.value.priorityTrainings.unshift(training as Training);
    }
  };

  const removePriorityTrainingLocal = (id: string) => {
    state.value.priorityTrainings = state.value.priorityTrainings.filter((t) => t.id !== id);
  };

  return {
    // State
    myTrainings: computed(() => state.value.myTrainings),
    managerTrainings: computed(() => state.value.managerTrainings),
    priorityTrainings: computed(() => state.value.priorityTrainings),

    // User actions
    fetchMyTrainings,
    getMyTraining,
    createTraining,
    updateTraining,
    deleteTraining,

    // Manager actions
    fetchManagerTrainings,
    verifyTraining,
    fetchPriorityTrainings,
    sendReminder,

    // Local mutation methods (for SSE handlers)
    updateMyTrainingLocal,
    addOrUpdateMyTrainingLocal,
    removeMyTrainingLocal,
    addOrUpdateManagerTrainingLocal,
    removeManagerTrainingLocal,
    addOrUpdatePriorityTrainingLocal,
    removePriorityTrainingLocal,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTrainingStore, import.meta.hot));
}
